import type {
  Application,
  ApplicationNote,
  ApplicationDocument,
  ApplicationQuickView,
  ApplicationId,
  CalendarEvent,
  CalendarEventFilter,
  ChildApplicationId,
  TimelineEvent,
  UserId,
  UpdateChildApplicationInput,
  UpdateEnrollmentProgressInput,
  UpdateMedicalRecordInput,
  UpdateUserPreferencesInput,
  CompleteEnrollmentInput,
  CreateNoteInput,
  UpdateNoteInput,
  DeleteNoteInput,
  UploadDocumentInput,
  NoteId,
  DocumentId,
} from '@triedge/admissions-domain';
import type { ResourceId } from '@triedge/domain-core/types';
import {
  addDocument,
  addNote,
  askAdmissionsAI as askAdmissionsAIRequest,
  assignApplication,
  completeEnrollment,
  getApplication,
  getApplicationQuickView,
  getUserPreferences,
  listCalendarEvents,
  listApplications,
  listTimelineEvents,
  sendApplicationMessage,
  lookupApplication,
  scheduleInterview,
  scheduleVisit,
  submitApplication,
  updateApplicationStatus,
  updateChildApplication,
  updateEnrollmentProgress,
  updateMedicalRecord,
  updateNote,
  updateUserPreferences,
  deleteNote,
} from '../api/admissions';
import type {
  AdmissionsAIContext,
  AdmissionsAIResponse,
  ConversationMessage,
} from '../api/admissions';
import {
  mapDomainApplicationToUi,
  mapDomainMedicalToUi,
  mapFormToSubmitInput,
  mapUiDocumentToDomain,
  mapUiNoteToDomain,
  toDomainGradeLevel,
  toDomainStatus,
  toUiGradeLabel,
  toUiStatus,
  toUnixSeconds,
} from './ui-mappers';
import type {
  UiApplication,
  UiApplicationStatus,
  UiApplicationQuickView,
  UiCalendarEvent,
  UiDashboardPreferences,
  UiDocument,
  UiGrandparentInfo,
  UiParent,
  UiFormFamily,
  UiFormStudent,
  UiNote,
  UiEnrollmentProgress,
  UiStudent,
  UiTimelineEvent,
} from './ui-types';
import { toIsoDate, toIsoDateTime } from '../shared/timestamp-mappers';

type UiAdapterConfig = {
  academicYear?: string;
  useLocalStore?: boolean;
  localStoreKey?: string;
  logEvents?: boolean;
};

const adapterConfig: UiAdapterConfig = {
  academicYear: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
  useLocalStore: false,
  localStoreKey: 'triedge_admissions_local_store',
  logEvents: false,
};

const asApplicationId = (id: string): ApplicationId => id as ApplicationId;
const asChildApplicationId = (id: string): ChildApplicationId => id as ChildApplicationId;
const asUserId = (id: string): UserId => id as UserId;
const asNoteId = (id: string): NoteId => id as NoteId;
const asDocumentId = (id: string): DocumentId => id as DocumentId;
const asResourceId = (id: string): ResourceId => id as ResourceId;
const hasWindow = (): boolean => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
const isLocalHost = (): boolean => {
  if (!hasWindow()) {
    return false;
  }
  const host = window.location.hostname;
  if (!host) {
    return false;
  }
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '0.0.0.0') {
    return true;
  }
  return host.endsWith('.local');
};
const resolveLocalStoreFlag = (value: boolean): boolean => {
  if (!value) {
    return false;
  }
  if (!isLocalHost()) {
    console.warn('[TriedgeBridge] useLocalStore disabled outside local environments.');
    return false;
  }
  return true;
};

let localCache: UiApplication[] | null = null;

const createFallbackUuid = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (token) => {
    const value = Math.floor(Math.random() * 16);
    const next = token === 'x' ? value : (value % 4) + 8;
    return next.toString(16);
  });
};

const createUuid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return createFallbackUuid();
};

const emitEvent = (name: string, detail: Record<string, unknown>): void => {
  if (!hasWindow()) {
    return;
  }
  if (typeof window.CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent('triedge:bridge-event', { detail: { name, ...detail } }));
  }
  if (adapterConfig.logEvents) {
    console.info('[TriedgeBridge]', name, detail);
  }
};

const getLocalStoreKey = () => adapterConfig.localStoreKey ?? 'triedge_admissions_local_store';

const loadLocalApplications = (): UiApplication[] => {
  if (!hasWindow()) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(getLocalStoreKey());
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed as UiApplication[];
    }
    if (parsed && Array.isArray(parsed.applications)) {
      return parsed.applications as UiApplication[];
    }
  } catch {
    return [];
  }
  return [];
};

const saveLocalApplications = (applications: UiApplication[]): void => {
  if (!hasWindow()) {
    return;
  }
  try {
    window.localStorage.setItem(getLocalStoreKey(), JSON.stringify(applications));
  } catch (error) {
    console.warn('[TriedgeBridge] Failed to persist local applications', error);
  }
};

const getLocalApplications = (): UiApplication[] | null => {
  if (!adapterConfig.useLocalStore || !hasWindow()) {
    return null;
  }
  if (!localCache) {
    localCache = loadLocalApplications();
  }
  return localCache;
};

const setLocalApplications = (applications: UiApplication[]): void => {
  if (!adapterConfig.useLocalStore || !hasWindow()) {
    return;
  }
  localCache = applications;
  saveLocalApplications(applications);
};

type LocalUpdateResult = { applications: UiApplication[]; updated: UiApplication | null };

const updateLocalApplications = (
  updater: (applications: UiApplication[]) => LocalUpdateResult
): LocalUpdateResult | null => {
  const localApps = getLocalApplications();
  if (!localApps) {
    return null;
  }
  const { applications, updated } = updater([...localApps]);
  setLocalApplications(applications);
  return { applications, updated };
};

const stripAssignedTo = (student: UiStudent): UiStudent => {
  const { assignedTo: _assignedTo, ...rest } = student;
  return rest;
};

const stripEnrollmentDeadline = (student: UiStudent): UiStudent => {
  const { enrollmentDeadline: _enrollmentDeadline, ...rest } = student;
  return rest;
};

const stripMedical = (student: UiStudent): UiStudent => {
  const { medical: _medical, ...rest } = student;
  return rest;
};

const splitName = (fullName: string, fallbackLast: string): { firstName: string; lastName: string } => {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return { firstName: '', lastName: fallbackLast };
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length < 2) {
    return { firstName: parts[0] ?? '', lastName: '' };
  }
  const lastName = parts[parts.length - 1] ?? fallbackLast;
  const firstName = parts.slice(0, -1).join(' ');
  return { firstName, lastName };
};

const makeReferenceCode = (): string => {
  const yearPrefix = adapterConfig.academicYear?.split('-')[0] ?? `${new Date().getFullYear()}`;
  const suffix = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `YST-${yearPrefix}-${suffix}`;
};

const buildLocalParents = (family: UiFormFamily): UiParent[] => {
  const surname = family.surname || 'Name required';
  const father = splitName(family.fatherName, surname);
  const mother = splitName(family.motherName, surname);
  const fallbackEmail = family.fatherEmail || family.motherEmail || '';
  const fallbackPhone = family.fatherCell || family.motherCell || family.homePhone || '';

  const fatherParent: UiParent = {
    firstName: father.firstName,
    lastName: father.lastName,
    email: family.fatherEmail || fallbackEmail,
    phone: family.fatherCell || fallbackPhone,
    relation: 'Father',
    isPrimary: true,
  };
  if (family.fatherTitle) fatherParent.title = family.fatherTitle;
  if (family.fatherOccupation) fatherParent.occupation = family.fatherOccupation;
  if (family.fatherFirm) fatherParent.firm = family.fatherFirm;

  const motherParent: UiParent = {
    firstName: mother.firstName,
    lastName: mother.lastName,
    email: family.motherEmail || fallbackEmail,
    phone: family.motherCell || fallbackPhone,
    relation: 'Mother',
    isPrimary: true,
  };
  if (family.motherTitle) motherParent.title = family.motherTitle;
  if (family.motherOccupation) motherParent.occupation = family.motherOccupation;
  if (family.motherFirm) motherParent.firm = family.motherFirm;
  if (family.motherMaidenName) motherParent.maidenName = family.motherMaidenName;

  return [fatherParent, motherParent];
};

const normalizeGender = (value: string): UiStudent['gender'] => {
  if (value === 'Male' || value === 'Female' || value === 'Other') {
    return value;
  }
  return 'Male';
};

const buildLocalStudents = (students: UiFormStudent[], surname: string): UiStudent[] => {
  return students.map((student) => {
    const studentLast = student.lastName || surname || '';
    const entry: UiStudent = {
      id: createUuid(),
      firstName: student.firstName || '',
      lastName: studentLast,
      dob: student.dob || new Date().toISOString().slice(0, 10),
      gender: normalizeGender(student.gender),
      gradeApplying: student.grade || student.currentGrade || 'Kindergarten',
      status: 'Submitted',
    };

    if (student.middleName) entry.middleName = student.middleName;
    if (student.hebrewFirstName) entry.hebrewFirstName = student.hebrewFirstName;
    if (student.hebrewMiddleName) entry.hebrewMiddleName = student.hebrewMiddleName;
    if (student.hebrewLastName) entry.hebrewLastName = student.hebrewLastName;
    if (student.calledBy) entry.calledBy = student.calledBy;
    if (student.hebrewDob) entry.hebrewDob = student.hebrewDob;
    if (student.currentGrade) entry.currentGrade = student.currentGrade;
    if (student.placeOfBirth) entry.placeOfBirth = student.placeOfBirth;
    if (student.languageAtHome) entry.languageAtHome = student.languageAtHome;
    if (student.currentSchool) entry.currentSchool = student.currentSchool;
    if (student.principalName) entry.principalName = student.principalName;
    if (student.principalPhone) entry.principalPhone = student.principalPhone;
    if (student.pediatricianName) entry.pediatricianName = student.pediatricianName;
    if (student.pediatricianPhone) entry.pediatricianPhone = student.pediatricianPhone;
    if (student.dentistName) entry.dentistName = student.dentistName;
    if (student.dentistPhone) entry.dentistPhone = student.dentistPhone;

    return entry;
  });
};

const normalizeLocalString = (value: string): string => value.replace(/\s+/g, ' ').trim();

const buildLocalGrandparentInfo = (
  info: UiFormFamily['paternalGrandparents']
): UiGrandparentInfo | undefined => {
  const grandfather = normalizeLocalString(`${info.grandfatherTitle} ${info.grandfatherName}`.trim());
  const grandmother = normalizeLocalString(`${info.grandmotherTitle} ${info.grandmotherName}`.trim());
  const names = [grandfather, grandmother].filter(Boolean).join(' & ');
  const location = normalizeLocalString(`${info.city} ${info.state}`.trim());
  if (!names && !location && !info.phone && !info.lastName) {
    return undefined;
  }
  const result: UiGrandparentInfo = {
    names,
    location,
  };
  if (info.phone) result.phone = info.phone;
  if (info.lastName) result.family = info.lastName;
  return result;
};

const buildLocalApplication = (family: UiFormFamily, students: UiFormStudent[]): UiApplication => {
  const surname = family.surname || 'Name required';
  const application: UiApplication = {
    id: createUuid(),
    referenceCode: makeReferenceCode(),
    familyName: surname,
    address: family.address,
    city: family.city,
    state: family.state,
    zip: family.zip,
    homePhone: family.homePhone,
    students: buildLocalStudents(students, surname),
    parents: buildLocalParents(family),
    submittedAt: new Date().toISOString(),
    status: 'Submitted',
    notes: [],
    documents: [],
  };

  if (family.shul) application.synagogue = family.shul;
  if (family.rav) application.rav = family.rav;
  if (family.maritalStatus) application.maritalStatus = family.maritalStatus;
  if (family.isAdopted) application.adoptedChild = family.isAdopted;
  if (family.isStepParent) application.stepParent = family.isStepParent;
  const paternalGrandparents = buildLocalGrandparentInfo(family.paternalGrandparents);
  if (paternalGrandparents) application.paternalGrandparents = paternalGrandparents;
  const maternalGrandparents = buildLocalGrandparentInfo(family.maternalGrandparents);
  if (maternalGrandparents) application.maternalGrandparents = maternalGrandparents;

  return application;
};

const mapTimelineEventToUi = (event: TimelineEvent): UiTimelineEvent => ({
  id: event.id,
  eventType: event.eventType,
  occurredAt: event.occurredAt,
  summary: event.summary,
  ...(event.actorRole ? { actorRole: event.actorRole } : {}),
  ...(event.metadata ? { metadata: event.metadata } : {}),
});

const mapDocumentToUi = (doc: ApplicationDocument): UiDocument => {
  const mapped: UiDocument = {
    id: doc.id,
    resourceId: doc.resourceId,
  };
  if (doc.fileName) mapped.name = doc.fileName;
  if (doc.contentType) mapped.contentType = doc.contentType;
  if (typeof doc.sizeBytes === 'number') mapped.sizeBytes = doc.sizeBytes;
  if (Array.isArray(doc.childApplicationIds) && doc.childApplicationIds.length > 0) {
    mapped.childApplicationIds = [...doc.childApplicationIds];
  }
  if (doc.storageProvider) mapped.storageProvider = doc.storageProvider;
  if (doc.storageKey) mapped.storageKey = doc.storageKey;
  if (doc.storageBucket) mapped.storageBucket = doc.storageBucket;
  if (doc.storageRegion) mapped.storageRegion = doc.storageRegion;
  return mapped;
};

const mapQuickViewToUi = (view: ApplicationQuickView): UiApplicationQuickView => ({
  applicationId: view.applicationId,
  familyName: view.familyName,
  referenceCode: view.referenceCode,
  students: view.students.map((student) => ({
    id: student.id,
    firstName: student.firstName,
    lastName: student.lastName,
    gradeApplying: toUiGradeLabel(student.gradeApplying),
    dateOfBirth: student.dateOfBirth ?? null,
  })),
  documents: (view.documents ?? []).map(mapDocumentToUi),
  timeline: (view.timeline ?? []).map(mapTimelineEventToUi),
});

const timelineCache = new Map<string, UiTimelineEvent[]>();

const addTimelineEntry = (applicationId: string, summary: string): void => {
  const next: UiTimelineEvent = {
    id: `timeline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    eventType: 'StatusUpdate',
    occurredAt: Math.floor(Date.now() / 1000),
    summary,
  };
  const existing = timelineCache.get(applicationId) ?? [];
  timelineCache.set(applicationId, [next, ...existing].slice(0, 50));
};

const mergeTimeline = (applicationId: string, items: UiTimelineEvent[]): UiTimelineEvent[] => {
  const cached = timelineCache.get(applicationId);
  if (!cached?.length) {
    return items;
  }
  const seen = new Set(items.map((item) => item.id));
  const merged = [...cached.filter((item) => !seen.has(item.id)), ...items];
  return merged;
};

const mapCalendarEventToUi = (event: CalendarEvent): UiCalendarEvent => ({
  id: event.id,
  applicationId: event.applicationId,
  childApplicationId: event.childApplicationId ?? null,
  eventType: event.eventType === 'interview' ? 'Interview' : 'Visit',
  startDateTime: event.startDateTime,
  endDateTime: event.endDateTime,
  ...(event.referenceCode ? { referenceCode: event.referenceCode } : {}),
  ...(event.familyName ? { familyName: event.familyName } : {}),
});

const toCalendarTimestamp = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return null;
  return Math.floor(parsed / 1000);
};

const getAuthUserKey = (): string | null => {
  if (!hasWindow()) {
    return null;
  }
  try {
    const keys = Object.keys(window.localStorage);
    const lastAuthKey = keys.find((key) => key.endsWith('.LastAuthUser'));
    if (!lastAuthKey) {
      return null;
    }
    const value = window.localStorage.getItem(lastAuthKey);
    if (!value) {
      return null;
    }
    return value.replace(/[^a-z0-9@._-]+/gi, '_');
  } catch {
    return null;
  }
};

const getLocalPreferencesKey = () => {
  const base = `${getLocalStoreKey()}_preferences`;
  const userKey = getAuthUserKey();
  return userKey ? `${base}_${userKey}` : base;
};

const loadLocalPreferences = (): UiDashboardPreferences | null => {
  if (!hasWindow()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(getLocalPreferencesKey());
    if (!raw) return null;
    return JSON.parse(raw) as UiDashboardPreferences;
  } catch {
    return null;
  }
};

const saveLocalPreferences = (preferences: UiDashboardPreferences): void => {
  if (!hasWindow()) {
    return;
  }
  try {
    window.localStorage.setItem(getLocalPreferencesKey(), JSON.stringify(preferences));
  } catch (error) {
    console.warn('[TriedgeBridge] Failed to persist local preferences', error);
  }
};

const normalizeAiStatus = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const mapped = toDomainStatus(trimmed as UiApplicationStatus);
  if (mapped) return mapped;
  return trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '_');
};

const normalizeAiGrades = (values?: string[]): string[] | undefined => {
  if (!values || !Array.isArray(values)) return undefined;
  const mapped = values
    .map((grade) => toDomainGradeLevel(grade) ?? grade.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_'))
    .filter((grade) => grade.length > 0);
  return mapped.length ? mapped : undefined;
};

const normalizeAiContext = (context?: AdmissionsAIContext): AdmissionsAIContext | undefined => {
  if (!context) return undefined;
  const status = context.status?.map(normalizeAiStatus).filter((value): value is string => !!value);
  const grade = normalizeAiGrades(context.grade);
  const normalized: AdmissionsAIContext = {};
  if (status?.length) normalized.status = status;
  if (grade?.length) normalized.grade = grade;
  if (context.assignedTo) normalized.assignedTo = context.assignedTo;
  if (context.search) normalized.search = context.search;
  if (context.selectedIds?.length) normalized.selectedIds = context.selectedIds;
  if (context.currentView) normalized.currentView = context.currentView;
  if (context.personaRole) normalized.personaRole = context.personaRole;
  if (context.personaName) normalized.personaName = context.personaName;
  if (context.activeApplicationId) normalized.activeApplicationId = context.activeApplicationId;
  if (context.activeApplicationCode) normalized.activeApplicationCode = context.activeApplicationCode;
  if (context.activeStudentId) normalized.activeStudentId = context.activeStudentId;
  if (context.activeStudentName) normalized.activeStudentName = context.activeStudentName;
  if (context.faqReference) normalized.faqReference = context.faqReference;
  return Object.keys(normalized).length ? normalized : undefined;
};

export const uiAdapter = {
  configure(config: UiAdapterConfig) {
    if (config.academicYear) {
      adapterConfig.academicYear = config.academicYear;
    }
    if (typeof config.useLocalStore === 'boolean') {
      const nextUseLocalStore = resolveLocalStoreFlag(config.useLocalStore);
      if (adapterConfig.useLocalStore !== nextUseLocalStore) {
        localCache = null;
      }
      adapterConfig.useLocalStore = nextUseLocalStore;
    }
    if (config.localStoreKey) {
      if (adapterConfig.localStoreKey !== config.localStoreKey) {
        localCache = null;
      }
      adapterConfig.localStoreKey = config.localStoreKey;
    }
    if (typeof config.logEvents === 'boolean') {
      adapterConfig.logEvents = config.logEvents;
    }
  },

  async listApplications(options?: {
    status?: UiApplicationStatus[];
    grade?: string;
    assignedTo?: string;
    search?: string;
  }): Promise<UiApplication[]> {
    const localApps = getLocalApplications();
    if (localApps) {
      let items = [...localApps];
      if (options?.status?.length) {
        items = items.filter((app) => options.status?.includes(app.status));
      }
      if (options?.grade) {
        const target = options.grade.trim();
        items = items.filter((app) =>
          app.students.some((student) => student.gradeApplying === target)
        );
      }
      if (options?.assignedTo) {
        items = items.filter(
          (app) =>
            app.assignedTo === options.assignedTo
            || app.students.some((student) => student.assignedTo === options.assignedTo)
        );
      }
      if (options?.search) {
        const term = options.search.trim().toLowerCase();
        items = items.filter(
          (app) =>
            app.familyName.toLowerCase().includes(term)
            || app.referenceCode.toLowerCase().includes(term)
            || app.students.some((student) =>
              `${student.firstName} ${student.lastName}`.toLowerCase().includes(term)
            )
        );
      }
      emitEvent('listApplications', { count: items.length, source: 'localStore' });
      return items;
    }
    const statusFilter = options?.status?.length ? options.status.map(toDomainStatus) : undefined;
    const gradeFilter = options?.grade ? toDomainGradeLevel(options.grade) : null;
    const assignedToFilter = options?.assignedTo ? asUserId(options.assignedTo) : null;
    const filter = {
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(gradeFilter ? { gradeLevel: [gradeFilter] } : {}),
      ...(assignedToFilter ? { assignedTo: assignedToFilter } : {}),
      ...(options?.search ? { search: options.search.trim() } : {}),
    };
    const response = await listApplications(Object.keys(filter).length ? { filter } : {});
    const items = response.items.map(mapDomainApplicationToUi);
    emitEvent('listApplications', { count: items.length });
    return items;
  },

  async askAdmissionsAI(
    question: string,
    conversationHistory: ConversationMessage[] = [],
    context?: AdmissionsAIContext,
    model?: string
  ): Promise<AdmissionsAIResponse> {
    const normalizedContext = normalizeAiContext(context);
    const response = await askAdmissionsAIRequest(question, conversationHistory, normalizedContext, model);
    const selectedModel = (
      response.debug
      && typeof response.debug === 'object'
      && response.debug.model
      && typeof response.debug.model === 'object'
      && typeof (response.debug.model as { key?: unknown }).key === 'string'
    )
      ? (response.debug.model as { key: string }).key
      : null;
    emitEvent('askAdmissionsAI', {
      citations: response.citations.length,
      requestedModel: model ?? null,
      selectedModel,
      responseDecision: (
        response.debug
        && typeof response.debug === 'object'
        && typeof (response.debug as { responseDecision?: unknown }).responseDecision === 'string'
      )
        ? (response.debug as { responseDecision: string }).responseDecision
        : null,
    });
    return response;
  },

  async getApplication(id: string): Promise<UiApplication> {
    const localApps = getLocalApplications();
    if (localApps) {
      const found = localApps.find((app) => app.id === id);
      if (!found) {
        throw new Error('Application not found');
      }
      emitEvent('getApplication', { applicationId: found.id, source: 'localStore' });
      return found;
    }
    const application = await getApplication(id);
    const mapped = mapDomainApplicationToUi(application);
    emitEvent('getApplication', { applicationId: mapped.id, source: 'appsync' });
    return mapped;
  },

  async lookupStatus(code: string, familyName: string): Promise<UiApplication | null> {
    const localApps = getLocalApplications();
    if (localApps) {
      const normalizedFamily = familyName.trim().toLowerCase();
      const found = localApps.find(
        (app) => app.referenceCode === code && app.familyName.toLowerCase() === normalizedFamily
      );
      emitEvent('lookupStatus', { referenceCode: code, found: Boolean(found), source: 'localStore' });
      return found ?? null;
    }
    const result = await lookupApplication(code, familyName);
    if (!result.found || !result.application) return null;
    const mapped = mapDomainApplicationToUi(result.application);
    emitEvent('lookupStatus', { applicationId: mapped.id, referenceCode: mapped.referenceCode, source: 'appsync' });
    return mapped;
  },

  async submitApplicationFromForm(
    family: UiFormFamily,
    students: UiFormStudent[],
    academicYearOverride?: string
  ): Promise<UiApplication> {
    const localApps = getLocalApplications();
    if (localApps) {
      const application = buildLocalApplication(family, students);
      const next = [application, ...localApps];
      setLocalApplications(next);
      emitEvent('submitApplication', {
        applicationId: application.id,
        referenceCode: application.referenceCode,
        source: 'localStore',
      });
      return application;
    }
    const academicYear = academicYearOverride ?? adapterConfig.academicYear ?? '';
    const input = mapFormToSubmitInput(family, students, academicYear);
    const application = await submitApplication(input);
    const mapped = mapDomainApplicationToUi(application);
    emitEvent('submitApplication', {
      applicationId: mapped.id,
      referenceCode: mapped.referenceCode,
      source: 'appsync',
    });
    return mapped;
  },

  async updateApplicationStatus(applicationId: string, status: UiApplicationStatus): Promise<Application> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) =>
        app.id === applicationId ? { ...app, status } : app
      );
      const updated = next.find((app) => app.id === applicationId) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Application not found');
      }
      emitEvent('updateApplicationStatus', { applicationId, status, source: 'localStore' });
      return localUpdate.updated as unknown as Application;
    }

    const response = await updateApplicationStatus({
      applicationId: asApplicationId(applicationId),
      status: toDomainStatus(status),
    });
    emitEvent('updateApplicationStatus', { applicationId, status, source: 'appsync' });
    return response;
  },

  async assignApplication(applicationId: string, staffId: string): Promise<Application> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) =>
        app.id === applicationId ? { ...app, assignedTo: staffId } : app
      );
      const updated = next.find((app) => app.id === applicationId) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Application not found');
      }
      emitEvent('assignApplication', { applicationId, staffId, source: 'localStore' });
      return localUpdate.updated as unknown as Application;
    }

    const response = await assignApplication({ applicationId: asApplicationId(applicationId), staffId: asUserId(staffId) });
    emitEvent('assignApplication', { applicationId, staffId, source: 'appsync' });
    return response;
  },

  async scheduleInterview(applicationId: string, interviewDate: number): Promise<Application> {
    const localUpdate = updateLocalApplications((applications) => {
      const iso = toIsoDateTime(interviewDate) ?? new Date(interviewDate * 1000).toISOString();
      const next = applications.map((app) =>
        app.id === applicationId ? { ...app, interviewDate: iso } : app
      );
      const updated = next.find((app) => app.id === applicationId) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Application not found');
      }
      emitEvent('scheduleInterview', { applicationId, interviewDate, source: 'localStore' });
      return localUpdate.updated as unknown as Application;
    }

    const response = await scheduleInterview({ applicationId: asApplicationId(applicationId), interviewDate });
    emitEvent('scheduleInterview', { applicationId, interviewDate, source: 'appsync' });
    return response;
  },

  async scheduleVisit(applicationId: string, visitDate: number): Promise<Application> {
    const localUpdate = updateLocalApplications((applications) => {
      const iso = toIsoDateTime(visitDate) ?? new Date(visitDate * 1000).toISOString();
      const next = applications.map((app) =>
        app.id === applicationId ? { ...app, visitDate: iso } : app
      );
      const updated = next.find((app) => app.id === applicationId) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Application not found');
      }
      emitEvent('scheduleVisit', { applicationId, visitDate, source: 'localStore' });
      return localUpdate.updated as unknown as Application;
    }

    const response = await scheduleVisit({ applicationId: asApplicationId(applicationId), visitDate });
    emitEvent('scheduleVisit', { applicationId, visitDate, source: 'appsync' });
    return response;
  },

  async updateStudent(input: UpdateChildApplicationInput): Promise<unknown> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => {
        const studentIndex = app.students.findIndex((student) => student.id === input.childApplicationId);
        if (studentIndex === -1) {
          return app;
        }
        const students = [...app.students];
        const student = students[studentIndex];
        if (!student) {
          return app;
        }
        let nextStudent = student;
        if (input.status) {
          nextStudent = { ...nextStudent, status: toUiStatus(input.status) };
        }
        if (input.assignedTo !== undefined) {
          if (input.assignedTo === null) {
            nextStudent = stripAssignedTo(nextStudent);
          } else {
            nextStudent = { ...nextStudent, assignedTo: input.assignedTo };
          }
        }
        if (input.enrollmentDeadline !== undefined) {
          if (input.enrollmentDeadline === null) {
            nextStudent = stripEnrollmentDeadline(nextStudent);
          } else {
            const deadlineIso = toIsoDate(input.enrollmentDeadline);
            if (deadlineIso) {
              nextStudent = { ...nextStudent, enrollmentDeadline: deadlineIso };
            } else {
              nextStudent = stripEnrollmentDeadline(nextStudent);
            }
          }
        }
        students[studentIndex] = nextStudent;
        return { ...app, students };
      });
      const updated = next.find((app) => app.students.some((student) => student.id === input.childApplicationId)) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Student not found');
      }
      emitEvent('updateStudent', { childApplicationId: input.childApplicationId, source: 'localStore' });
      return localUpdate.updated;
    }

    return updateChildApplication(input);
  },

  async updateStudentStatus(studentId: string, status: UiApplicationStatus): Promise<unknown> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => {
        const studentIndex = app.students.findIndex((student) => student.id === studentId);
        if (studentIndex === -1) {
          return app;
        }
        const students = [...app.students];
        const current = students[studentIndex];
        if (!current) {
          return app;
        }
        students[studentIndex] = { ...current, status };
        return { ...app, students };
      });
      const updated = next.find((app) => app.students.some((student) => student.id === studentId)) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Student not found');
      }
      addTimelineEntry(localUpdate.updated.id, `Status: ${status}`);
      emitEvent('updateStudentStatus', { studentId, status, source: 'localStore' });
      return localUpdate.updated;
    }

    const response = await updateChildApplication({
      childApplicationId: asChildApplicationId(studentId),
      status: toDomainStatus(status),
    });
    if (response?.applicationId) {
      addTimelineEntry(response.applicationId, `Status: ${status}`);
    }
    emitEvent('updateStudentStatus', { studentId, status, source: 'appsync' });
    return response;
  },

  async updateStudentAssignment(studentId: string, assignedTo: string | null): Promise<unknown> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => {
        const studentIndex = app.students.findIndex((student) => student.id === studentId);
        if (studentIndex === -1) {
          return app;
        }
        const students = [...app.students];
        const current = students[studentIndex];
        if (!current) {
          return app;
        }
        if (assignedTo === null) {
          students[studentIndex] = stripAssignedTo(current);
        } else {
          students[studentIndex] = { ...current, assignedTo };
        }
        return { ...app, students };
      });
      const updated = next.find((app) => app.students.some((student) => student.id === studentId)) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Student not found');
      }
      emitEvent('updateStudentAssignment', { studentId, assignedTo, source: 'localStore' });
      return localUpdate.updated;
    }

    const response = await updateChildApplication({
      childApplicationId: asChildApplicationId(studentId),
      assignedTo: assignedTo ? asUserId(assignedTo) : null,
    });
    emitEvent('updateStudentAssignment', { studentId, assignedTo, source: 'appsync' });
    return response;
  },

  async updateStudentEnrollmentDeadline(studentId: string, deadline?: string): Promise<unknown> {
    const parsed = toUnixSeconds(deadline);
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => {
        const studentIndex = app.students.findIndex((student) => student.id === studentId);
        if (studentIndex === -1) {
          return app;
        }
        const students = [...app.students];
        const current = students[studentIndex];
        if (!current) {
          return app;
        }
        if (!parsed) {
          students[studentIndex] = stripEnrollmentDeadline(current);
        } else {
          const deadlineIso = toIsoDate(parsed);
          if (deadlineIso) {
            students[studentIndex] = { ...current, enrollmentDeadline: deadlineIso };
          } else {
            students[studentIndex] = stripEnrollmentDeadline(current);
          }
        }
        return { ...app, students };
      });
      const updated = next.find((app) => app.students.some((student) => student.id === studentId)) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Student not found');
      }
      emitEvent('updateStudentEnrollmentDeadline', { studentId, deadline: parsed ?? null, source: 'localStore' });
      return localUpdate.updated;
    }

    const response = await updateChildApplication({
      childApplicationId: asChildApplicationId(studentId),
      enrollmentDeadline: parsed ?? null,
    });
    emitEvent('updateStudentEnrollmentDeadline', { studentId, deadline: parsed ?? null, source: 'appsync' });
    return response;
  },

  async updateEnrollmentProgress(input: UpdateEnrollmentProgressInput): Promise<unknown> {
    const localUpdate = updateLocalApplications((applications) => {
      const nowIso = new Date().toISOString();
      const next = applications.map((app) => {
        const studentIndex = app.students.findIndex((student) => student.id === input.childApplicationId);
        if (studentIndex === -1) {
          return app;
        }
        const students = [...app.students];
        const current = students[studentIndex];
        if (!current) {
          return app;
        }
        const existing = current.enrollmentProgress;
        const progress: UiEnrollmentProgress = {
          step1AcceptanceLetter: existing?.step1AcceptanceLetter ?? false,
          step2RegistrationForm: existing?.step2RegistrationForm ?? false,
          step3B6TForm: existing?.step3B6TForm ?? false,
          step4TuitionContract: existing?.step4TuitionContract ?? false,
          step5Payment: existing?.step5Payment ?? false,
          step6ImmunizationRecords: existing?.step6ImmunizationRecords ?? false,
        };
        if (existing?.formSubmittedAt !== undefined) {
          progress.formSubmittedAt = existing.formSubmittedAt;
        }
        if (existing?.contractSignedAt !== undefined) {
          progress.contractSignedAt = existing.contractSignedAt;
        }
        if (existing?.paymentConfirmedAt !== undefined) {
          progress.paymentConfirmedAt = existing.paymentConfirmedAt;
        }
        if (existing?.immunizationVerified !== undefined) {
          progress.immunizationVerified = existing.immunizationVerified;
        }
        progress[input.step] = input.completed;
        if (input.completed) {
          if (input.step === 'step2RegistrationForm' && !progress.formSubmittedAt) {
            progress.formSubmittedAt = nowIso;
          }
          if (input.step === 'step4TuitionContract' && !progress.contractSignedAt) {
            progress.contractSignedAt = nowIso;
          }
          if (input.step === 'step5Payment' && !progress.paymentConfirmedAt) {
            progress.paymentConfirmedAt = nowIso;
          }
          if (input.step === 'step6ImmunizationRecords') {
            progress.immunizationVerified = true;
          }
        } else if (input.step === 'step6ImmunizationRecords') {
          progress.immunizationVerified = false;
        }
        students[studentIndex] = { ...current, enrollmentProgress: progress };
        return { ...app, students };
      });
      const updated = next.find((app) => app.students.some((student) => student.id === input.childApplicationId)) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Student not found');
      }
      emitEvent('updateEnrollmentProgress', { childApplicationId: input.childApplicationId, step: input.step, source: 'localStore' });
      return localUpdate.updated;
    }

    const response = await updateEnrollmentProgress(input);
    emitEvent('updateEnrollmentProgress', { childApplicationId: input.childApplicationId, step: input.step, source: 'appsync' });
    return response;
  },

  async updateMedicalRecord(input: UpdateMedicalRecordInput): Promise<unknown> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => {
        const studentIndex = app.students.findIndex((student) => student.id === input.childApplicationId);
        if (studentIndex === -1) {
          return app;
        }
        const students = [...app.students];
        const current = students[studentIndex];
        if (!current) {
          return app;
        }
        const medical = mapDomainMedicalToUi(input.medical);
        const nextStudent = medical ? { ...current, medical } : stripMedical(current);
        students[studentIndex] = {
          ...nextStudent,
        };
        return { ...app, students };
      });
      const updated = next.find((app) => app.students.some((student) => student.id === input.childApplicationId)) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Student not found');
      }
      emitEvent('updateMedicalRecord', { childApplicationId: input.childApplicationId, source: 'localStore' });
      return localUpdate.updated;
    }

    const response = await updateMedicalRecord(input);
    emitEvent('updateMedicalRecord', { childApplicationId: input.childApplicationId, source: 'appsync' });
    return response;
  },

  async completeEnrollment(input: CompleteEnrollmentInput): Promise<Application> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) =>
        app.id === input.applicationId ? { ...app, status: 'Enrolled' as UiApplicationStatus } : app
      );
      const updated = next.find((app) => app.id === input.applicationId) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      if (!localUpdate.updated) {
        throw new Error('Application not found');
      }
      emitEvent('completeEnrollment', { applicationId: input.applicationId, source: 'localStore' });
      return localUpdate.updated as unknown as Application;
    }

    const response = await completeEnrollment(input);
    emitEvent('completeEnrollment', { applicationId: input.applicationId, source: 'appsync' });
    return response;
  },

  async addNote(applicationId: string, note: UiNote): Promise<ApplicationNote> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => {
        if (app.id !== applicationId) {
          return app;
        }
        const nextNote: UiNote = {
          id: note.id || createUuid(),
          text: note.text,
          author: note.author || 'Staff',
          timestamp: note.timestamp || new Date().toISOString(),
          isImportant: note.isImportant ?? false,
        };
        return { ...app, notes: [...app.notes, nextNote] };
      });
      const updated = next.find((app) => app.id === applicationId) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      const fallback: ApplicationNote = {
        id: asNoteId(createUuid()),
        applicationId: asApplicationId(applicationId),
        authorId: asUserId(createUuid()),
        authorRole: 'staff',
        authorName: note.author || undefined,
        noteType: 'general',
        content: note.text,
        isImportant: note.isImportant ?? false,
        visibility: ['staff', 'nurse', 'admin'],
        createdAt: Math.floor(Date.now() / 1000),
        updatedAt: Math.floor(Date.now() / 1000),
      };
      emitEvent('addNote', { applicationId, noteId: fallback.id, source: 'localStore' });
      return fallback;
    }

    const mapped = mapUiNoteToDomain(note);
    const notePayload: Omit<CreateNoteInput, 'applicationId' | 'authorName'> & { authorName?: string } = {
      noteType: 'general',
      content: mapped.content,
      isImportant: mapped.isImportant,
      ...(mapped.authorName ? { authorName: mapped.authorName } : {}),
    };
    const payload: {
      applicationId: ApplicationId;
      note: Omit<CreateNoteInput, 'applicationId' | 'authorName'> & { authorName?: string };
    } = {
      applicationId: asApplicationId(applicationId),
      note: notePayload,
    };
    const response = await addNote(payload);
    emitEvent('addNote', { applicationId, noteId: response.id, source: 'appsync' });
    return response;
  },

  async sendApplicationMessage(applicationId: string, subject: string, message: string): Promise<unknown> {
    const localUpdate = getLocalApplications();
    if (localUpdate) {
      emitEvent('sendApplicationMessage', { applicationId, subject, source: 'localStore' });
      return { ok: true };
    }

    const response = await sendApplicationMessage({ applicationId, subject, message });
    emitEvent('sendApplicationMessage', { applicationId, subject, source: 'appsync' });
    return response;
  },

  async updateNote(input: UpdateNoteInput): Promise<ApplicationNote> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => {
        const notes = app.notes.map((item) =>
          item.id === input.noteId
            ? {
                ...item,
                ...(input.content ? { text: input.content } : {}),
                ...(input.isImportant !== undefined ? { isImportant: input.isImportant } : {}),
              }
            : item
        );
        return { ...app, notes };
      });
      const updated = next.find((app) => app.notes.some((note) => note.id === input.noteId)) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      const fallback: ApplicationNote = {
        id: asNoteId(input.noteId),
        applicationId: asApplicationId(createUuid()),
        authorId: asUserId(createUuid()),
        authorRole: 'staff',
        noteType: 'general',
        content: input.content ?? '',
        isImportant: input.isImportant ?? false,
        visibility: ['staff', 'nurse', 'admin'],
        createdAt: Math.floor(Date.now() / 1000),
        updatedAt: Math.floor(Date.now() / 1000),
      };
      emitEvent('updateNote', { noteId: input.noteId, source: 'localStore' });
      return fallback;
    }

    const response = await updateNote(input);
    emitEvent('updateNote', { noteId: input.noteId, source: 'appsync' });
    return response;
  },

  async deleteNote(input: DeleteNoteInput): Promise<boolean> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => ({
        ...app,
        notes: app.notes.filter((note) => note.id !== input.noteId),
      }));
      return { applications: next, updated: null };
    });
    if (localUpdate) {
      emitEvent('deleteNote', { noteId: input.noteId, deleted: true, source: 'localStore' });
      return true;
    }

    const response = await deleteNote(input);
    emitEvent('deleteNote', { noteId: input.noteId, deleted: response, source: 'appsync' });
    return response;
  },

  async addDocument(applicationId: string, document: UiDocument): Promise<ApplicationDocument> {
    const localUpdate = updateLocalApplications((applications) => {
      const next = applications.map((app) => {
        if (app.id !== applicationId) {
          return app;
        }
        const nextDoc: UiDocument = {
          ...document,
          id: document.id || createUuid(),
          resourceId: document.resourceId || `resource-${createUuid()}`,
        };
        return { ...app, documents: [...app.documents, nextDoc] };
      });
      const updated = next.find((app) => app.id === applicationId) ?? null;
      return { applications: next, updated };
    });
    if (localUpdate) {
      const fallback: ApplicationDocument = {
        id: asDocumentId(document.id || createUuid()),
        applicationId: asApplicationId(applicationId),
        resourceId: asResourceId(document.resourceId || `resource-${createUuid()}`),
        category: 'other',
        ...(document.name ? { fileName: document.name } : {}),
        ...(document.contentType ? { contentType: document.contentType } : {}),
        ...(Number.isFinite(document.sizeBytes)
          ? { sizeBytes: Math.max(0, Math.round(Number(document.sizeBytes))) }
          : {}),
        ...(Array.isArray(document.childApplicationIds) && document.childApplicationIds.length > 0
          ? { childApplicationIds: document.childApplicationIds.map((value) => asChildApplicationId(String(value))) }
          : {}),
        ...(document.storageProvider ? { storageProvider: document.storageProvider } : {}),
        ...(document.storageKey ? { storageKey: document.storageKey } : {}),
        ...(document.storageBucket ? { storageBucket: document.storageBucket } : {}),
        ...(document.storageRegion ? { storageRegion: document.storageRegion } : {}),
        uploadedBy: asUserId(createUuid()),
        uploadedAt: Math.floor(Date.now() / 1000),
        createdAt: Math.floor(Date.now() / 1000),
        updatedAt: Math.floor(Date.now() / 1000),
      };
      emitEvent('addDocument', { applicationId, documentId: fallback.id, source: 'localStore' });
      return fallback;
    }

    const documentPayload: Omit<UploadDocumentInput, 'applicationId'> = mapUiDocumentToDomain(document);
    const payload: { applicationId: ApplicationId; document: Omit<UploadDocumentInput, 'applicationId'> } = {
      applicationId: asApplicationId(applicationId),
      document: documentPayload,
    };
    const response = await addDocument(payload);
    emitEvent('addDocument', { applicationId, documentId: response.id, source: 'appsync' });
    return response;
  },

  async listTimelineEvents(applicationId: string): Promise<UiTimelineEvent[]> {
    const localApps = getLocalApplications();
    if (localApps) {
      if (!localApps.some((item) => item.id === applicationId)) {
        return [];
      }
      return [];
    }
    const response = await listTimelineEvents({ filter: { applicationId: asApplicationId(applicationId) } });
    return mergeTimeline(applicationId, response.items.map(mapTimelineEventToUi));
  },

  async getApplicationQuickView(applicationId: string): Promise<UiApplicationQuickView> {
    const localApps = getLocalApplications();
    if (localApps) {
      const app = localApps.find((item) => item.id === applicationId);
      if (!app) {
        throw new Error('Application not found');
      }
      emitEvent('getApplicationQuickView', { applicationId: app.id, source: 'localStore' });
      return {
        applicationId: app.id,
        familyName: app.familyName,
        referenceCode: app.referenceCode,
        students: app.students.map((student) => ({
          id: student.id,
          firstName: student.firstName,
          lastName: student.lastName,
          gradeApplying: student.gradeApplying,
          dateOfBirth: toUnixSeconds(student.dob),
        })),
        documents: app.documents,
        timeline: [],
      };
    }
    const quickView = await getApplicationQuickView(applicationId);
    emitEvent('getApplicationQuickView', { applicationId, source: 'appsync' });
    const mapped = mapQuickViewToUi(quickView);
    return {
      ...mapped,
      timeline: mergeTimeline(applicationId, mapped.timeline),
    };
  },

  async listCalendarEvents(filter?: { applicationId?: string; eventTypes?: UiCalendarEvent['eventType'][] }): Promise<UiCalendarEvent[]> {
    const localApps = getLocalApplications();
    if (localApps) {
      const events: UiCalendarEvent[] = [];
      const apps = filter?.applicationId
        ? localApps.filter((app) => app.id === filter.applicationId)
        : localApps;
      for (const app of apps) {
        const interviewTs = toCalendarTimestamp(app.interviewDate);
        if (interviewTs) {
          events.push({
            id: `interview-${app.id}`,
            applicationId: app.id,
            childApplicationId: app.students[0]?.id ?? null,
            eventType: 'Interview',
            startDateTime: interviewTs,
            endDateTime: interviewTs + 3600,
            referenceCode: app.referenceCode,
            familyName: app.familyName,
          });
        }
        const visitTs = toCalendarTimestamp(app.visitDate);
        if (visitTs) {
          events.push({
            id: `visit-${app.id}`,
            applicationId: app.id,
            childApplicationId: app.students[0]?.id ?? null,
            eventType: 'Visit',
            startDateTime: visitTs,
            endDateTime: visitTs + 3600,
            referenceCode: app.referenceCode,
            familyName: app.familyName,
          });
        }
      }
      if (!filter?.eventTypes?.length) {
        emitEvent('listCalendarEvents', { count: events.length, source: 'localStore' });
        return events;
      }
      const allowed = new Set(filter.eventTypes);
      const filtered = events.filter((event) => allowed.has(event.eventType));
      emitEvent('listCalendarEvents', { count: filtered.length, source: 'localStore' });
      return filtered;
    }

    const eventTypeFilter = filter?.eventTypes?.length
      ? filter.eventTypes.map((type) => (type === 'Interview' ? 'interview' : 'visit'))
      : undefined;
    const response = await listCalendarEvents({
      filter: {
        ...(filter?.applicationId ? { applicationId: asApplicationId(filter.applicationId) } : {}),
        ...(eventTypeFilter ? { eventTypes: eventTypeFilter as CalendarEventFilter['eventTypes'] } : {}),
      },
    });
    const mapped = response.map(mapCalendarEventToUi);
    emitEvent('listCalendarEvents', { count: mapped.length, source: 'appsync' });
    return mapped;
  },

  async getUserPreferences(): Promise<UiDashboardPreferences> {
    const localApps = getLocalApplications();
    if (localApps) {
      return loadLocalPreferences() ?? { defaultView: 'list', filters: {} };
    }
    const cached = loadLocalPreferences();
    if (cached) {
      return cached;
    }
    const response = await getUserPreferences();
    const normalizedFilters = (() => {
      if (typeof response.filters === 'string') {
        try {
          const parsed = JSON.parse(response.filters);
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
          return {};
        }
      }
      return response.filters ?? {};
    })();
    const preferences = {
      defaultView: response.defaultView,
      filters: normalizedFilters,
    };
    saveLocalPreferences(preferences);
    return preferences;
  },

  async updateUserPreferences(preferences: Partial<UiDashboardPreferences>): Promise<UiDashboardPreferences> {
    const localApps = getLocalApplications();
    if (localApps) {
      const existing = loadLocalPreferences() ?? { defaultView: 'list', filters: {} };
      const mergedFilters = preferences.filters ?? existing.filters ?? {};
      const merged: UiDashboardPreferences = {
        defaultView: preferences.defaultView ?? existing.defaultView,
        filters: mergedFilters,
      };
      saveLocalPreferences(merged);
      emitEvent('updateUserPreferences', { source: 'localStore' });
      return merged;
    }
    const payload: UpdateUserPreferencesInput = {
      ...(preferences.defaultView ? { defaultView: preferences.defaultView } : {}),
      ...(preferences.filters ? { filters: preferences.filters } : {}),
    };
    const response = await updateUserPreferences(payload);
    const merged: UiDashboardPreferences = {
      defaultView: response.defaultView,
      filters: response.filters ?? {},
    };
    saveLocalPreferences(merged);
    emitEvent('updateUserPreferences', { source: 'appsync' });
    return merged;
  },
};

export type UiAdapter = typeof uiAdapter;
